import React from 'react';
import { TouchableOpacity } from 'react-native';
import { ShoppingCard, ORANGE_ACTION } from '@cubemeet/ui';
import { MdShoppingBag } from 'react-icons/md';
import { mergePayload, coalesceText, buildMetaPairs, deriveImageUrl } from './itineraryUtils';

interface Props {
  it: any;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onOpenPoi?: (payload: any) => void;
  dayDate?: string;
  onImageClick?: (images: any[], startIndex: number) => void;
}

const ShoppingAdapter: React.FC<Props> = ({ it, isExpanded = false, onOpenPoi, dayDate, onImageClick }) => {
  const hasCardObj = Boolean(it?.card && Object.keys(it.card || {}).length > 0) || Boolean(it?.card_id || it?.cardId || (it?.meta && (it.meta.cardId || it.meta.card_id)));
  const mergedPayload = hasCardObj ? mergePayload(it) : (it?.custom_payload || it?.inline_payload || it?.payload || {});

  const imageUrl = deriveImageUrl(it, mergedPayload);
  
  const title = coalesceText(it?.title, it?.card?.title, mergedPayload?.title) || '未命名购物';
  
  // Create action icon directly like QuickAddMenu does
  const shoppingIcon = <MdShoppingBag style={{ color: ORANGE_ACTION, fontSize: 16 }} />;
  
  // 優先使用 it.note（行程表副本的描述），再使用 it.description（前端更新後的）
  const description = coalesceText(
    it?.note,
    it?.description,
    mergedPayload.description,
    it?.card?.description,
    mergedPayload.summary,
    mergedPayload.intro
  );

  // Extract address/location information
  const extractString = (value: any): string | undefined => {
    if (value == null) return undefined;
    if (typeof value === 'string') return value.trim() || undefined;
    if (Array.isArray(value)) {
      const parts = value.map(v => extractString(v)).filter(Boolean) as string[];
      return parts.length ? parts.join(' ') : undefined;
    }
    if (typeof value === 'object') {
      const candidates = [
        'formatted_address', 'formattedAddress', 'address', 'display', 'label', 'name', 'title', 'short_name', 'description', 'locality'
      ];
      for (const k of candidates) {
        if (value[k]) {
          const s = extractString(value[k]);
          if (s) return s;
        }
      }
    }
    return undefined;
  };

  // Prioritize mergedPayload.address (includes custom_payload override) over it.card.address
  const address = extractString(
    mergedPayload.address ||
    it?.card?.address ||
    mergedPayload.location ||
    mergedPayload.venue ||
    mergedPayload.place
  );

  // Shopping-specific fields
  let priceRange = mergedPayload.price_range || mergedPayload.priceRange;
  let category = mergedPayload.category || mergedPayload.shop_type || mergedPayload.shopType;
  let venue: string | undefined = undefined;
  const brands = mergedPayload.brands || mergedPayload.available_brands;
  let openingHours = mergedPayload.opening_hours || mergedPayload.openingHours || mergedPayload.hours;
  let phone = coalesceText(mergedPayload.phone, mergedPayload.telephone, it?.card?.phone);

  const metaPairs = buildMetaPairs(it, mergedPayload);
  const findMetaValue = (...labels: string[]): string | undefined => {
    for (const raw of labels) {
      const target = raw.trim().toLowerCase();
      if (!target) continue;
      const match = metaPairs.find((p) => {
        if (!p?.label) return false;
        return String(p.label).trim().toLowerCase() === target;
      });
      if (match && match.value) {
        const value = String(match.value).trim();
        if (value) return value;
      }
    }
    return undefined;
  };

  if (!category) {
    category = findMetaValue('购物类型', '購物類型', '购物分類', '購物分類', '购物类别', '類型', '类型');
  }
  if (!priceRange) {
    priceRange = findMetaValue('价位', '價位', '消费水平', '消費水平', '预算', '預算');
  }
  if (!venue) {
    venue = findMetaValue('购物地点', '購物地點', '购物位置', '購物位置', '购物场所', '購物場所', '地点', '地點');
  }
  if (!openingHours) {
    openingHours = findMetaValue('营业时间', '營業時間', '开放时间', '開放時間');
  }
  if (!phone) {
    phone = findMetaValue('电话', '電話');
  }

  const formattedBrands = Array.isArray(brands) ? brands.filter(Boolean).map((b: any) => String(b)).join('、') : (typeof brands === 'string' ? brands : undefined);

  if (category && !metaPairs.find((p) => p.label === '类型')) metaPairs.unshift({ label: '类型', value: category });
  if (priceRange && !metaPairs.find((p) => p.label === '价位')) metaPairs.push({ label: '价位', value: priceRange });
  if (formattedBrands && !metaPairs.find((p) => p.label === '推荐品牌')) metaPairs.push({ label: '推荐品牌', value: formattedBrands });

  const transportNote = (() => {
    const item = metaPairs.find((p) => {
      if (!p || !p.label) return false;
      const label = String(p.label).trim();
      return label === '交通方式' || label === '交通';
    });
    if (!item || !item.value) return undefined;
    return String(item.value).trim();
  })();

  const payloadForModal = {
    title,
    address,
    description,
    priceRange,
    category,
    brands: formattedBrands,
    openingHours,
    phone,
    imageUrl,
    metaPairs,
    card: it?.card,
    id: it?.card?.id || it?.meta?.cardId || it?.cardId,
    cardId: it?.card?.id || it?.meta?.cardId || it?.cardId,
  };

  const handlePress = () => {
    if (typeof onOpenPoi === 'function') {
      onOpenPoi(payloadForModal);
      return;
    }
    if (typeof onToggleExpand === 'function') onToggleExpand?.();
  };

  // Render card using dedicated ShoppingCard component
  const card = (
    <ShoppingCard
      title={title}
      note={description}
      venue={venue}
      category={category}
      priceRange={priceRange}
      address={address}
      phone={phone}
      openingHours={openingHours}
      transportNote={transportNote}
      actionIcon={shoppingIcon}
      imageUrl={imageUrl}
    />
  );

  if (typeof onOpenPoi === 'function') {
    return (
      <TouchableOpacity activeOpacity={0.8} onPress={handlePress}>
        {card}
      </TouchableOpacity>
    );
  }

  return card;
};

export default ShoppingAdapter;